'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Sidebar } from '@/components/sidebar';
import { Header } from '@/components/header';
import { MetricsTimeRangeControls } from '@/components/metrics-time-range-controls';
import { MetricsRefreshControls, type MetricsViewActions } from '@/components/metrics-refresh-controls';
import { MetricsStorageUsage } from '@/components/metrics-storage-usage';
import type { MetricsTimeRangeKey } from '@/lib/metrics-time-range';
import { ChartProvider, useChartContext } from '@/app/rate-limit-metrics/ChartContext';
import { PasteGrafanaRangeButton } from '@/app/rate-limit-metrics/components/PasteGrafanaRangeButton';
import { useWorkloadMetrics } from './hooks/useWorkloadMetrics';
import { calculateAverageIntervalDurationSeconds, countDroppedRecords } from './logic';
import {
  ALL_GROUPING_DIMENSIONS,
  DEFAULT_WORKLOAD_INSIGHT_STATE,
  RANGE_PRESETS,
  WORKLOAD_CHART_METRICS,
  type ChartPairConfig,
  type RangeKey,
  type WorkloadInsightState,
} from './types';
import { WorkloadChartPair } from './components/WorkloadChartPair';
import { WorkloadInsights } from './components/WorkloadInsights';
import type { WorkloadMetricsSnapshot } from '@/lib/metrics-snapshot';

interface SharedWorkloadRange {
  fromMs: number;
  toMs: number | null;
  durationMs: number;
  presetLabel: string | null;
  live: boolean;
  setLive: (value: boolean) => void;
}

export function WorkloadMetricsContent({
  sharedRange,
  showHeader = true,
  onActionsChange,
  onSnapshotChange,
  snapshot = null,
}: {
  sharedRange?: SharedWorkloadRange;
  showHeader?: boolean;
  onActionsChange?: (actions: MetricsViewActions | null) => void;
  onSnapshotChange?: (snapshot: WorkloadMetricsSnapshot | null) => void;
  snapshot?: WorkloadMetricsSnapshot | null;
} = {}) {
  const [initialNow] = useState(() => Date.now());
  const [rangeKey, setRangeKey] = useState<RangeKey | null>('15m');
  const [durationMs, setDurationMs] = useState(
    () => RANGE_PRESETS.find((preset) => preset.key === '15m')!.durationMs,
  );
  const [endMs, setEndMs] = useState<number | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [chartPairs, setChartPairs] = useState<ChartPairConfig[]>(() => (
    snapshot?.chartPairs ?? [{
      id: 1,
      dimensions: [...ALL_GROUPING_DIMENSIONS],
      metrics: [...WORKLOAD_CHART_METRICS],
      clients: null,
    }]
  ));
  const nextChartPairId = useRef(
    Math.max(0, ...(snapshot?.chartPairs.map((pair) => pair.id) ?? [1])) + 1,
  );
  const [hiddenSeriesByPair, setHiddenSeriesByPair] = useState<Record<number, string[]>>(
    () => snapshot?.hiddenSeriesByPair ?? {},
  );
  const [insightState, setInsightState] = useState<WorkloadInsightState>(() => (
    snapshot?.insightState ?? {
      dimensions: [...DEFAULT_WORKLOAD_INSIGHT_STATE.dimensions],
      latencyDimensions: [...DEFAULT_WORKLOAD_INSIGHT_STATE.latencyDimensions],
      latencyMetric: DEFAULT_WORKLOAD_INSIGHT_STATE.latencyMetric,
    }
  ));
  const { zoomBus } = useChartContext();
  const activeDurationMs = sharedRange?.durationMs ?? durationMs;
  const activeEndMs = sharedRange ? sharedRange.toMs : endMs;
  const activeAutoRefresh = snapshot === null && (sharedRange?.live ?? autoRefresh);
  const {
    intervals,
    loading,
    error,
    lastUpdatedMs,
    loadedFromMs,
    loadedToMs,
    refetch,
  } = useWorkloadMetrics({
    durationMs: activeDurationMs,
    endMs: activeEndMs,
    autoRefresh: activeAutoRefresh,
    snapshot,
  });

  useEffect(() => {
    onActionsChange?.({ loading, refresh: refetch });
  }, [loading, onActionsChange, refetch]);
  useEffect(() => () => onActionsChange?.(null), [onActionsChange]);

  useEffect(() => {
    if (loading || lastUpdatedMs === null || loadedFromMs === null || loadedToMs === null) {
      onSnapshotChange?.(null);
      return;
    }
    onSnapshotChange?.({
      intervals,
      chartPairs,
      hiddenSeriesByPair,
      insightState,
      lastUpdatedMs,
      loadedFromMs,
      loadedToMs,
    });
  }, [
    chartPairs,
    hiddenSeriesByPair,
    insightState,
    intervals,
    lastUpdatedMs,
    loadedFromMs,
    loadedToMs,
    loading,
    onSnapshotChange,
  ]);
  useEffect(() => () => onSnapshotChange?.(null), [onSnapshotChange]);

  const droppedRecords = useMemo(() => countDroppedRecords(intervals), [intervals]);
  const aggregateCount = intervals.reduce((sum, interval) => sum + interval.entries.length, 0);
  const averageIntervalDurationSeconds = calculateAverageIntervalDurationSeconds(
    activeDurationMs,
    intervals.length,
  );

  const displayToMs = activeEndMs ?? loadedToMs ?? initialNow;
  const displayFromMs = sharedRange
    ? sharedRange.toMs === null ? displayToMs - activeDurationMs : sharedRange.fromMs
    : endMs === null ? loadedFromMs ?? displayToMs - durationMs : endMs - durationMs;
  const activePresetLabel = sharedRange
    ? sharedRange.presetLabel
    : rangeKey === null ? null : RANGE_PRESETS.find((preset) => preset.key === rangeKey)!.label;
  const rangeDescription = activePresetLabel
    ?? `${new Date(displayFromMs).toLocaleTimeString()} – ${new Date(displayToMs).toLocaleTimeString()}`;
  const updatedDescription = lastUpdatedMs === null ? 'Updated —' : `Updated ${new Date(lastUpdatedMs).toLocaleTimeString()}`;

  // Keep the chart viewport aligned with presets, pasted ranges, history,
  // and the shared controls. Data and the visible x-axis must move together.
  useEffect(() => {
    zoomBus.broadcast('__workload-range-controls__', activeEndMs === null
      ? { 'xaxis.autorange': true }
      : { 'xaxis.range[0]': new Date(displayFromMs), 'xaxis.range[1]': new Date(displayToMs) },
      false,
    );
  }, [activeEndMs, displayFromMs, displayToMs, zoomBus]);

  const setPreset = (key: MetricsTimeRangeKey) => {
    const preset = RANGE_PRESETS.find((candidate) => candidate.key === key)!;
    setRangeKey(key);
    setDurationMs(preset.durationMs);
    setEndMs(null);
  };

  const setCustomRange = (fromMs: number, toMs: number) => {
    if (toMs <= fromMs) return;
    setRangeKey(null);
    setDurationMs(toMs - fromMs);
    setEndMs(toMs);
    setAutoRefresh(false);
  };

  const addChartPair = (client: string | null = null) => {
    const id = nextChartPairId.current++;
    setChartPairs((current) => [
      {
        id,
        dimensions: [...ALL_GROUPING_DIMENSIONS],
        metrics: [...WORKLOAD_CHART_METRICS],
        clients: client === null ? null : [client],
      },
      ...current,
    ]);
  };

  const updateChartPair = (updated: ChartPairConfig) => {
    setChartPairs((current) => current.map((pair) => pair.id === updated.id ? updated : pair));
  };

  const removeChartPair = (id: number) => {
    setChartPairs((current) => current.filter((pair) => pair.id !== id));
  };

  const updateHiddenSeries = useCallback((id: number, hiddenSeries: string[]) => {
    setHiddenSeriesByPair((current) => {
      const existing = current[id] ?? [];
      if (existing.length === hiddenSeries.length
        && existing.every((name, index) => name === hiddenSeries[index])) return current;
      return { ...current, [id]: hiddenSeries };
    });
  }, []);

  return (
    <main className={`flex-1 overflow-auto px-4 pb-12 ${showHeader ? 'pt-4' : 'pt-1'}`}>
      <div className="mb-3 min-w-0">
        {showHeader && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-xl font-bold">Workload Metrics</h1>
            <MetricsTimeRangeControls
              fromMs={displayFromMs}
              toMs={displayToMs}
              onSetFromTo={setCustomRange}
              onSetPreset={setPreset}
              activePresetLabel={activePresetLabel}
              onSetNow={() => setEndMs(null)}
            >
              <PasteGrafanaRangeButton
                onApply={setCustomRange}
                setLiveTail={(value) => {
                  if (!value) setAutoRefresh(false);
                }}
              />
              <MetricsRefreshControls
                label="Auto-refresh"
                checked={activeAutoRefresh}
                onCheckedChange={sharedRange ? sharedRange.setLive : setAutoRefresh}
                loading={loading}
                onRefresh={refetch}
                disabled={!sharedRange && endMs !== null}
              />
            </MetricsTimeRangeControls>
          </div>
        )}
        <div className={`flex flex-wrap items-center gap-2 ${showHeader ? 'mt-1' : ''}`}>
          <p className="text-xs text-muted-foreground">
            {updatedDescription} · {rangeDescription} · {intervals.length} intervals
            {averageIntervalDurationSeconds === null ? '' : ` · ${averageIntervalDurationSeconds.toLocaleString()} sec/interval`}
            {' · '}{aggregateCount.toLocaleString()} aggregates
          </p>
          {snapshot === null
            ? <MetricsStorageUsage kind="workload" />
            : <span className="rounded-md border px-1.5 py-1 text-[11px] text-muted-foreground">Snapshot data</span>}
        </div>
      </div>

      <div className="space-y-3">
        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {droppedRecords > 0 && (
          <div className="flex gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {droppedRecords.toLocaleString()} workload records were dropped because an interval reached its cardinality limit. Totals in this window are incomplete.
            </span>
          </div>
        )}

        {!loading && intervals.length === 0 && !error && (
          <div className="rounded-md border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
            No workload data was recorded in this window. Try another time range.
          </div>
        )}

        {chartPairs.map((pair, index) => (
          <WorkloadChartPair
            key={pair.id}
            config={pair}
            intervals={intervals}
            loading={loading}
            canRemove={chartPairs.length > 1}
            onChange={updateChartPair}
            onRemove={() => removeChartPair(pair.id)}
            onAdd={index === 0 ? () => addChartPair() : undefined}
            onAddForClient={addChartPair}
            onHiddenSeriesChange={updateHiddenSeries}
            initialHiddenSeries={hiddenSeriesByPair[pair.id]}
          />
        ))}

        <WorkloadInsights
          intervals={intervals}
          loading={loading}
          state={insightState}
          onStateChange={setInsightState}
        />
      </div>
    </main>
  );
}

export default function WorkloadMetricsPage() {
  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />
        <ChartProvider>
          <WorkloadMetricsContent />
        </ChartProvider>
      </div>
    </div>
  );
}
